import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import {
  GET_SURVEY_LIST,
  GET_SURVEY_LIST_URL,
  GET_SURVEY_DETAIL,
  GET_SURVEY_DETAIL_URL,
  CREATE_SURVEY,
  CREATE_SURVEY_URL,
  UPDATE_SURVEY,
  UPDATE_SURVEY_URL,
  POST,
  GET,
  PUT,
  UPDATE_SURVEY_QUESTION_URL,
  REVIEW_SURVEY_URL,
  DELETE,
  DELETE_SURVEY_URL,
  DUPLICATE_SURVEY,
  DUPLICATE_SURVEY_URL,
} from "../../Api";
import apiRequest from "../../Api/connector";
import { STATUS_200 } from "../../Api/constants";
import {
  SurveyListResponse,
  SurveyDetailResponse,
  SurveyPayload,
  SurveyCreateUpdateResponse,
  ReviewSurveyPayload,
} from "../../Api/entities/SurveyEntity";
import { SomethingWentWrong } from "../../utils/Constant";
import { showToast } from "../../utils/helper/helper";

interface SurveyState {
  surveyList: SurveyListResponse | null;
  surveyDetails: SurveyDetailResponse | any;
  createSurveyResponse: SurveyCreateUpdateResponse | null;
  updateSurveyResponse: SurveyCreateUpdateResponse | null;
  duplicateSurveyResponse: SurveyCreateUpdateResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: SurveyState = {
  surveyList: null,
  surveyDetails: null,
  createSurveyResponse: null,
  updateSurveyResponse: null,
  duplicateSurveyResponse: null,
  loading: false,
  error: null,
};

export const getSurveyList = createAsyncThunk(
  GET_SURVEY_LIST,
  async ({ status, owner_id }: { status: string; owner_id?: string }) => {
    const response = await apiRequest(
      GET,
      GET_SURVEY_LIST_URL(status, owner_id)
    );
    return response.data;
  }
);

export const getSurveyDetail = createAsyncThunk(
  GET_SURVEY_DETAIL,
  async (id: number) => {
    const response = await apiRequest(GET, GET_SURVEY_DETAIL_URL(id));
    return response.data;
  }
);

export const createSurvey = createAsyncThunk(
  CREATE_SURVEY,
  async (payload: SurveyPayload) => {
    const response = await apiRequest(POST, CREATE_SURVEY_URL(), payload);
    return response.data;
  }
);

export const updateSurvey = createAsyncThunk(
  UPDATE_SURVEY,
  async (payload: SurveyPayload | any) => {
    const response = await apiRequest(PUT, UPDATE_SURVEY_URL(), payload);
    return response.data;
  }
);

export const createOrUpdateSurveyQuestion = createAsyncThunk(
  "survey/createOrUpdateSurveyQuestion",
  async (payload: { survey_id: number; section_id: number; question: any }) => {
    const response = await apiRequest(
      PUT,
      UPDATE_SURVEY_QUESTION_URL(),
      payload
    );
    return response.data;
  }
);

export const reviewSurvey = createAsyncThunk(
  "survey/reviewSurvey",
  async (payload: ReviewSurveyPayload) => {
    const response = await apiRequest(PUT, REVIEW_SURVEY_URL(), payload);
    return response;
  }
);

export const deleteSurvey = createAsyncThunk(
  "survey/deleteSurvey",
  async ({ id, type }: { id: string; type: string }) => {
    const response = await apiRequest(DELETE, DELETE_SURVEY_URL(id, type));
    return response;
  }
);

export const duplicateSurvey = createAsyncThunk(
  DUPLICATE_SURVEY,
  async (id: number) => {
    const response = await apiRequest(PUT, DUPLICATE_SURVEY_URL(), { id: id });
    return response.data;
  }
);

const SurveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getSurveyList.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getSurveyList.fulfilled,
        (state, action: PayloadAction<SurveyListResponse>) => {
          state.loading = false;
          state.surveyList = action.payload;
        }
      )
      .addCase(getSurveyList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSurveyDetail.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getSurveyDetail.fulfilled,
        (state, action: PayloadAction<SurveyDetailResponse>) => {
          state.loading = false;
          state.surveyDetails = action.payload;
        }
      )
      .addCase(getSurveyDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createSurvey.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSurvey.fulfilled, (state, action) => {
        state.loading = false;
        state.createSurveyResponse = action.payload;
        const { status_code, message } = action.payload as any;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(createSurvey.rejected, (state, action) => {
        state.loading = false;
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      .addCase(updateSurvey.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSurvey.fulfilled, (state, action) => {
        state.loading = false;
        state.updateSurveyResponse = action.payload;
      })
      .addCase(updateSurvey.rejected, (state, action) => {
        state.loading = false;
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      .addCase(deleteSurvey.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSurvey.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { status_code, message } = action.payload as any;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(deleteSurvey.rejected, (state, action) => {
        state.loading = false;
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      .addCase(duplicateSurvey.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateSurvey.fulfilled, (state, action) => {
        state.loading = false;
        state.duplicateSurveyResponse = action.payload;
      })
      .addCase(duplicateSurvey.rejected, (state, action) => {
        state.loading = false;
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });
  },
});

export default SurveySlice.reducer;
